import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { AppState, ShowRequest, RoleMgr } from '../../core';
import { AppTab } from '../../core/app-tab';
import { ScoutTaskDoc } from '../../core/schema/scout-task-schema';
import { PointDoc } from '../../core/schema/point-schema';
import { ScoutTaskService, BuildingService, PointService } from '../../core/services';
import { FesBaseComponent } from '../../shared/components/fes-base.component';
import { ScoutPath, ScoutPoint, TaskStatus } from "../../core/models"
import { ScoutPointRefDialogComponent } from "../../scout/scout-point/scout-point-ref-dialog/scout-point-ref-dialog.component"
import { first } from "rxjs/operators"


class MismatchedField {
  name : string
  scoutValue : any
  rxValue : any
  match : boolean
}

class MismatchedPoint {
  id: string
  fields : MismatchedField[] = []
  exists : boolean = true
  hasDuplicate : boolean = false
  scoutPath : ScoutPath
  scoutPoint : ScoutPoint
  scout : ScoutTaskDoc
  scoutList : ScoutTaskDoc[]
}

class ComparisonPoint {
    id : string = ''
    floorNum : string = ''
    quadrant : string = ''
    type : string = ''
    distFromWindow : string = ''
    floorAndDescript : string = ''
    locationDescription : string = ''
    wRef1 : string = ''
    wRef2 : string = ''
    latitude : number = 0
    longitude : number = 0
    altitudeEllipsoidMeters : number = 0
    windowCnt : string = ''
    floorToCeilingFtIn : string = ''
    clutter : string = ''
    hvac : string = ''
    scoutPath : ScoutPath
    scoutPoint : ScoutPoint
    scout : ScoutTaskDoc
}


@Component({
  selector: 'app-point-audit',
  templateUrl: './point-audit.component.html',
  styleUrls: ['./point-audit.component.scss']
})
export class PointAuditComponent extends FesBaseComponent implements OnInit, OnDestroy {

  isLoading : boolean = false;
  errorMsg: string = "";

  @Input() set tabConfig( tabConfig : AppTab ) {
    tabConfig.setSelected = this.onTabSelected
  }

  @Input('detailFn') public detailFn : (vi: ShowRequest) => void;

  scoutList : ScoutTaskDoc[] = []
  pointList : PointDoc[] = []

  buildingScouts : Map<string, ScoutTaskDoc[]> = new Map<string, ScoutTaskDoc[]>();
  buildingScoutPoints : Map<string, ComparisonPoint[]> = new Map<string, ComparisonPoint[]>();
  buildingRxDBPoints : Map<string, PointDoc[]> = new Map<string, PointDoc[]>();


  buildingMismatchedPoints : Map<string, MismatchedPoint[]> = new Map<string, MismatchedPoint[]>();

  scoutModal : ScoutPointRefDialogComponent

  constructor(appState:AppState, 
      public scoutTaskService: ScoutTaskService,
      public buildingService: BuildingService,
      public pointService: PointService,
      public modalService: BsModalService,
      public roleMgr: RoleMgr,
      private bsModalService: BsModalService) {
    super(appState);
  }

  ngOnInit(): void {
    this.initialize()
  }

  initialize() {
    this.isLoading = true

    this.scoutTaskService.doWhenReady(async () => {
      
      this.subscribe(this.scoutTaskService.rxCollection.find().$, async scouts => {
        this.scoutList = scouts
        await this.pointService.getAll().then(async points => {
          this.pointList = points
          await this.buildingService.getAll().then(buildings => {
            try{
              for( let i = 0; i < buildings?.length; i++ ) {
                this.buildingScouts.set(buildings[i].id, this.scoutList.filter(x => x.buildingId == buildings[i].id))
                this.buildingRxDBPoints.set(buildings[i].id, this.pointList.filter(x => x.buildingId == buildings[i].id))
              }
              this.buildingScouts.forEach(this.createBuildingPoints, this)
              this.auditPoints()
            } catch(error) {
              this.scoutTaskService.publishError(error, this)
            } finally {
              this.isLoading = false
            }
          })
        })
      })
    })
  }


  auditPoints() {
    this.buildingScoutPoints.forEach((value, key, map) => {
      if(value.length == 0) {
        //building scout has no paths yet
        return
      }
      let mismatchedPoints = []

      //check for dups first
      let duplicates = this.getDups(value)
      if(duplicates.length > 0) {
        const distinctScouts = [...new Set(value.map(item => item.scout._id))];
        const scoutsWithDups = []
        for( let i = 0; i < distinctScouts?.length; i++ ){
          scoutsWithDups.push(this.scoutList.find(scout => distinctScouts[i] == scout._id))
        }
        let duplicatePoint = new MismatchedPoint()
        duplicatePoint.hasDuplicate = true
        duplicatePoint.scoutList = scoutsWithDups
        mismatchedPoints.push(duplicatePoint)
        this.buildingMismatchedPoints.set(key, mismatchedPoints)
        return
      }

      for( let i = 0; i < value?.length; i++ ) {
        let misMatchedPoint = new MismatchedPoint()
        let scoutPoint = value[i]
        let rxDBPoint = this.buildingRxDBPoints.get(key).find(point => point._id == scoutPoint.id)
        misMatchedPoint.id = scoutPoint.id
        misMatchedPoint.scoutPath = scoutPoint.scoutPath
        misMatchedPoint.scoutPoint =scoutPoint.scoutPoint
        misMatchedPoint.scout = scoutPoint.scout

        if(rxDBPoint) {
          for (const [key, value] of Object.entries(scoutPoint)) {
            if(['scoutPath', 'scoutPoint', 'scout'].includes(key)) {
              continue;
            }
            let mismatchedField = new MismatchedField()
            if(key == 'id') {
              mismatchedField.name = key
              mismatchedField.scoutValue = value
              mismatchedField.rxValue = rxDBPoint['_id']
              mismatchedField.match = rxDBPoint['_id'] == value
            }else if(['latitude', 'longitude'].includes(key)) {
              mismatchedField.name = key
              mismatchedField.scoutValue = Number(value)
              mismatchedField.rxValue = Number(rxDBPoint[key])
              mismatchedField.match = Number(rxDBPoint[key]).toFixed(7) == Number(value).toFixed(7)
            }else if(['altitudeEllipsoidMeters'].includes(key)) {
              mismatchedField.name = key
              mismatchedField.scoutValue = Number(value).toFixed(3)
              mismatchedField.rxValue = Number(rxDBPoint[key]).toFixed(3)
              mismatchedField.match = Number(rxDBPoint[key]).toFixed(3) == Number(value).toFixed(3)
            }else if(['floorNum'].includes(key)) {
              mismatchedField.name = key
              mismatchedField.scoutValue = Number(value)
              mismatchedField.rxValue = Number(rxDBPoint[key])
              mismatchedField.match = Number(rxDBPoint[key]) == Number(value)
            }else if(['wRef1', 'wRef2', 'floorToCeilingFtIn'].includes(key)) {
              mismatchedField.name = key
              mismatchedField.scoutValue = Number(String(value).replace(/\D/g,''))
              mismatchedField.rxValue = Number(String(rxDBPoint[key]).replace(/\D/g,''))
              mismatchedField.match = Number(String(rxDBPoint[key]).replace(/\D/g,'')) == Number(String(value).replace(/\D/g,''))
            } else {
              mismatchedField.name = key
              mismatchedField.scoutValue = value
              mismatchedField.rxValue = rxDBPoint[key]
              mismatchedField.match = String(rxDBPoint[key]).trim() == String(value).trim()
            }
            misMatchedPoint.fields.push(mismatchedField)
          }
          if(!this.pointMatches(misMatchedPoint)){
            mismatchedPoints.push(misMatchedPoint)
          }
        } else {
          for (const [key, value] of Object.entries(scoutPoint)) {
            if(['scoutPath', 'scoutPoint', 'scout'].includes(key)) {
              continue;
            }
            let mismatchedField = new MismatchedField()
            if(key == 'id') {
              mismatchedField.name = key
              mismatchedField.scoutValue = value
            }else if(['floorNum', 'latitude', 'longitude', 'altitudeEllipsoidMeters'].includes(key)) {
              mismatchedField.name = key
              mismatchedField.scoutValue = Number(value)
            }else if(['wRef1', 'wRef2', 'floorToCeilingFtIn'].includes(key)) {
              mismatchedField.name = key
              mismatchedField.scoutValue = Number(String(value).replace(/\D/g,''))
            } else {
              mismatchedField.name = key
              mismatchedField.scoutValue = value
            }
            misMatchedPoint.fields.push(mismatchedField)
          }
          misMatchedPoint.exists = false
          mismatchedPoints.push(misMatchedPoint)
        }
      }
      if(mismatchedPoints.length > 0) {
        this.buildingMismatchedPoints.set(key, mismatchedPoints)
      }
    })
  }

  pointMatches(point: MismatchedPoint): Boolean {
    for( let i = 0; i < point.fields.length; i++ ) {
      if(!point.fields[i].match) {
        return false;
      }
    }
    return true;
  }


  createBuildingPoints(value, key) {
    if(value.length > 0) {
      this.buildingScoutPoints.set(key, [])
      for( let i = 0; i < value?.length; i++ ) {
        let extractedPoints = this.extractScoutPoints(value[i].scoutPaths, key, value[i])
        extractedPoints.forEach(point => this.buildingScoutPoints.get(key).push(point))
      }
    }
  }


  extractScoutPoints(scoutPaths: any[], buildingId: string, scout: ScoutTaskDoc): ComparisonPoint[] {
    let records = []

    function formatFloorNumber( floor : any ) {      
      if ( floor == null || floor == '' ) {
        return '---'
      }
      let numericFloor : number = parseFloat(floor)
      if ( isNaN(numericFloor) ) {
        return floor
      }
      var j = numericFloor % 10,
          k = numericFloor % 100;
      let suffix = ''
      if (j == 1 && k != 11) {
          suffix = "st";
      } else if (j == 2 && k != 12) {
          suffix = "nd";
      } else if (j == 3 && k != 13) {
          suffix = "rd";
      } else {
          suffix = "th"
      }
      return `${numericFloor}${suffix}`;
    }

    for( let i = 0; i < scoutPaths?.length; i++ ) {  
      const path = scoutPaths[i];
      for( let pointIndex = 0; pointIndex < path.scoutPoints?.length; pointIndex++ ) {
        const pt = path.scoutPoints[pointIndex];
        
        if ( pt?.type == 'VIA' ) {
          continue
        }

        let record:any = {
          path : path,
          point : pt
        };

        if ( pt.type == 'HA' && pt.pointId ) {
          record['pointId'] = pt.pointId;
        } else {
          record['pointId'] = pt.exPointId || pt.type
        }

        [ 'id', 'floor', 'modifiedDate', 'description' ].forEach( key => {
          record['path.' + key ] = path[key]
        })

        record['floorIdAndDescription'] = formatFloorNumber(path.floor) + ' Floor'
        const metersToFeet = ( metersVal) => {
          if ( isNaN(metersVal) ) {
            metersVal = parseFloat(metersVal)
          }
          return isNaN(metersVal) ? null : metersVal*3.2808
        }
        if ( pt ) {
          if ( pt.envData ) {
            const bInterior = pt.envData.bInterior
            const testPointType = bInterior != null ? ( bInterior ? 'Interior' : 'Exterior' ) : ''
            record['testPointType'] = testPointType
            record['floorIdAndDescription'] += ', ' + pt.name + ' ' + testPointType
            record['point.altitudeEllipsoidFeet'] = metersToFeet(pt.altitudeEllipsoidMeters)
          }
          Object.keys(pt).filter(key=>!['refData','envData'].includes(key)).forEach( key => {
            let cellValue = pt[key]
            record['point.' + key] = cellValue
          });
          ['refData','envData'].forEach( objKey => {
            const obj = pt[objKey]
            if ( obj ) {
              Object.keys(obj).forEach( key => {
                let cellValue = obj[key]                  
                record['point.' + objKey + '.' + key] = cellValue
              })
            }
          })
        }

        let point = new ComparisonPoint();
        point.floorNum = path.floor
        point.quadrant = pt.envData.quadrant
        point.type = record.testPointType
        point.distFromWindow = pt.envData.nearestWindowDistance.toFixed(2) + ' M'
        point.floorAndDescript = record.floorIdAndDescription
        point.id = record.pointId
        point.locationDescription = pt.envData.pointDescription
        point.wRef1 = pt.refData.location1Distance
        point.wRef2 = pt.refData.location2Distance
        point.latitude = pt.latitude.toFixed(7)
        point.longitude = pt.longitude.toFixed(7)
        point.altitudeEllipsoidMeters = pt.altitudeEllipsoidMeters
        point.windowCnt = pt.envData.windowCount
        point.floorToCeilingFtIn = pt.envData.floorToCeilingHeight
        point.clutter = pt.envData.clutterDescription
        point.hvac = pt.envData.hvacDescription
        point.scoutPath = record.path
        point.scoutPoint = record.point
        point.scout = scout

        if ( record ) {
          records.push( point )
        }
      }
    }
    return records
  }

  getDups(scoutPoints: ComparisonPoint[]) : ComparisonPoint[] {
    const lookup = scoutPoints.reduce((a, e) => {
      a[e.id] = ++a[e.id] || 0;
      return a;
    }, {});
    
    return scoutPoints.filter(e => lookup[e.id]);
  }

  showScout(scoutId: string, scout: ScoutTaskDoc) {
    this.appState.showView.next( new ShowRequest('tab','scout task', scoutId, scout) )
  }

  showScoutPoint( scoutPath: ScoutPath, scoutPoint: ScoutPoint, scout: ScoutTaskDoc ) {
    let uiLocked = TaskStatus.uiLockHandler(scoutPath.statusId, this.roleMgr.canEditUsers)
    const context : ModalOptions = {
      initialState: {
        scoutPointId: scoutPoint.id,
        scoutPathId: scoutPath.id,
        scoutTaskDoc: scout,
        allowEditMode: true,
        locked: uiLocked,
        hidePhotos: true
      },
      class: 'full-screen-modal',
      id: ScoutPointRefDialogComponent.ModalId,
      animated: true,      
      ignoreBackdropClick: true    
    } 
    const modalRef = this.bsModalService.show(ScoutPointRefDialogComponent, context );
    this.scoutModal = modalRef.content      
    const pointChangeSubscription = this.scoutModal.selectedPointChanged.subscribe(this.onScoutPointModalChange)
    this.scoutModal.onClosed.pipe(first()).subscribe( () => {
      this.scoutModal = null
      pointChangeSubscription?.unsubscribe()
    })
  }

  onScoutPointModalChange = ( pt:ScoutPoint ) => {}


  onTabSelected = ( bSelected : boolean ) => {
    console.log('point-audit tab selected: ' + bSelected )
    // if ( bSelected ) {
    //   this.initialize()
    // } else {
    //   this.unsubscribe()
    // }
  }

  ngOnDestroy(): void {
      this.destroy();
  }

}
